import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Date fields must be null (not "") for Pydantic Optional[date]
const DATE_FIELDS = new Set(["date", "delivered", "usps_claim"]);

const sanitizePayload = (payload) => {
  const result = {};
  for (const [key, value] of Object.entries(payload)) {
    if (DATE_FIELDS.has(key)) {
      result[key] = value === "" || value == null ? null : value;
    } else {
      result[key] = value === "" || value == null ? null : value;
    }
  }
  return result;
};

class MailLogService {
  /**
   * List mail log entries with optional search + pagination.
   * @returns {{ items: MailLogView[], total: number, limit: number, skip: number }}
   */
  getMailLog = async (search = "", limit = 50, skip = 0, filters = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    Object.entries(filters || {}).forEach(([key, values]) => {
      if (!Array.isArray(values) || values.length === 0) return;
      values.forEach((v) => params.append(key, v));
    });
    const { data } = await apiClient.get(`/api/mail-log?${params}`);
    return data;
  };

  getMailLogOptions = async () => {
    const { data } = await apiClient.get("/api/mail-log/options");
    return data;
  };

  /** Create a mail log row. payload = flat MailLogEntry fields object. */
  createMailLog = async (payload) => {
    try {
      const { data } = await apiClient.post(
        "/api/mail-log",
        sanitizePayload(payload),
      );
      return data; // MailLogView
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Failed to create mail log entry";
      throw new Error(msg);
    }
  };

  /** Update a mail log row. payload = flat MailLogEntry fields object. */
  updateMailLog = async (id, payload) => {
    try {
      const { data } = await apiClient.put(
        `/api/mail-log/${id}`,
        sanitizePayload(payload),
      );
      return data; // MailLogView
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Failed to update mail log entry";
      throw new Error(msg);
    }
  };

  /** Delete a mail log row. */
  deleteMailLog = async (id) => {
    const res = await apiClient.delete(`/api/mail-log/${id}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  /** Export mail log to Excel blob. */
  exportMailLog = async ({ search } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);

    const res = await apiClient.get(`/api/mail-log/export?${params}`, {
      responseType: "blob",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      validateStatus: (s) => s >= 200 && s < 300,
    });
    return res.data; // Blob
  };
}

export const mailLogService = new MailLogService();
